import { AfterViewChecked } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';

import {
  ChatAttachmentRef,
  ChatMessage,
  ChatPageContext,
  ChoiceOption,
  PendingActionEmbed,
  RichContentBlock
} from '@core/services/ai/ai-assistant.service';
import { AiAssistantService } from '@core/services/ai/ai-assistant.service';
import { AiPageContextProvider } from '@core/services/ai/ai-page-context.provider';
import { ChatAttachmentService } from '@core/services/data/chat-attachment.service';
import { WorkspaceStore } from '../../../shared/services/workspace.store';
import { HtmlSanitizerService } from '@core/services/infra/html-sanitizer.service';
import { markdownToHtml } from '../../../shared/utils/markdown.helper';
import { AiBlockHostComponent } from '../../../shared/components/ai-widgets/ai-block-host.component';
import { DataTableRowAction } from '../../../shared/components/ai-widgets/blocks/ai-data-table-block.component';
import { SupportPanelComponent } from '../../../shared/components/support-panel/support-panel.component';
import { SupportStore } from '@core/services/support/support.store';
import { UIFeedbackService } from '@shared/services/ui-feedback.service';
import { SafeHtml } from '@angular/platform-browser';
import {
  ChatSuggestion,
  emptyHintKeyFor,
  inputPlaceholderKeyFor,
  suggestionsForWorkspaceType
} from './chat-suggestions.util';

/** Which tab of the docked panel is showing. */
export type PanelTab = 'assistant' | 'support';

/**
 * How many assistant turns a handoff carries (#291).
 *
 * The tail, not the whole session: the server clamps the note at 8000 chars,
 * and a transcript truncated mid-sentence reads worse than a short one. Ten
 * turns covers the failed exchange that made the user ask for a human.
 */
const HANDOFF_MAX_MESSAGES = 10;

/**
 * AI assistant panel host for the global shell.
 *
 * The entry point is the sidenav link « Assistant Virtuel » (see
 * `NavigationLoaderService.assistantNavItem`). This component only hosts the
 * docked chat panel; it no longer renders a toolbar trigger or FAB.
 *
 * Reuses `AiAssistantService` for conversation state.
 */
@Component({
  selector: 'app-toolbar-ai-search',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    TranslatePipe,
    AiBlockHostComponent,
    SupportPanelComponent
  ],
  templateUrl: './toolbar-ai-search.component.html',
  styleUrls: ['./toolbar-ai-search.component.scss']
})
export class ToolbarAiSearchComponent implements AfterViewChecked {
  private readonly assistant = inject(AiAssistantService);
  private readonly pageContextProvider = inject(AiPageContextProvider);
  private readonly router = inject(Router);
  private readonly sanitizer = inject(HtmlSanitizerService);
  private readonly attachmentService = inject(ChatAttachmentService);
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly translate = inject(TranslateService);
  private readonly support = inject(SupportStore);
  private readonly feedback = inject(UIFeedbackService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Drag-over highlight for the drop zone. */
  readonly isDraggingFiles = signal(false);

  @ViewChild('messagesEnd') private messagesEnd?: ElementRef<HTMLDivElement>;
  @ViewChild('messagesContainer')
  private messagesContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('panelTextarea')
  private panelTextarea?: ElementRef<HTMLTextAreaElement>;

  readonly draft = signal('');
  readonly isFullscreen = signal(false);
  readonly isOpen = this.assistant.isOpen;
  readonly isAvailable = this.assistant.isAvailable;

  /**
   * Which tab the panel is showing.
   *
   * Defaults to the assistant, except where the assistant does not exist:
   * `CHAT_ENABLED_WORKSPACE_TYPES` excludes `service_provider` and `syndic`,
   * and those users still need a way to reach support — so the panel is hosted
   * whenever EITHER tab has content, and the assistant tab is simply absent
   * when unavailable.
   */
  readonly activeTab = signal<PanelTab>('assistant');
  readonly supportUnreadCount = this.support.unreadCount;
  /** The assistant tab only exists where the assistant does. */
  readonly showTabs = this.isAvailable;

  /**
   * Opened by the support side (bubble, notification deep-link) rather than by
   * `AiAssistantService.toggle()`.
   *
   * Kept as its own signal because the assistant's open state is gated on the
   * assistant being available, and a `service_provider` workspace must still be
   * able to open the panel to reach support.
   */
  private readonly supportOpen = signal(false);

  readonly panelOpen = computed(() => this.isOpen() || this.supportOpen());
  readonly sending = this.assistant.sending;
  readonly resolvingActionId = this.assistant.resolvingActionId;
  readonly messages = this.assistant.messages;
  readonly lastError = this.assistant.lastError;
  readonly hasMessages = this.assistant.hasMessages;
  readonly conversations = this.assistant.conversations;
  readonly currentConversationId = this.assistant.conversationId;
  readonly temporaryMode = this.assistant.temporaryMode;
  readonly historyQuery = signal('');
  readonly devUsage = this.assistant.devUsage;

  readonly filteredConversations = computed(() => {
    const q = this.historyQuery().trim().toLowerCase();
    const list = this.conversations();
    if (!q) return list;
    return list.filter((c) => {
      const label = this.conversationLabel(c).toLowerCase();
      return label.includes(q) || c.id.toLowerCase().includes(q);
    });
  });

  readonly canRetry = computed(
    () => !this.sending() && this.lastError() !== null
  );

  /** id of the message whose copy button is showing the "Copied" check. */
  readonly copiedMessageId = signal<string | null>(null);

  /** Files staged for the next message, before upload. */
  readonly pendingFiles = signal<File[]>([]);
  readonly attachmentError = signal<string | null>(null);
  readonly maxAttachments = this.attachmentService.maxFiles;
  readonly maxAttachmentBytes = this.attachmentService.maxBytesPerFile;
  readonly canAddMoreAttachments = computed(
    () => this.pendingFiles().length < this.maxAttachments
  );

  readonly visibleMessages = computed<ChatMessage[]>(() =>
    this.messages().filter((m) => {
      if (m.role !== 'user' && m.role !== 'assistant') return false;
      // Hide the empty streaming shell the backend creates to signal tool
      // activity — the typing indicator (with its "checking your data…"
      // label) represents that state; an empty bubble would just add a gap.
      if (
        m.role === 'assistant' &&
        m.streaming === true &&
        m.content.length === 0 &&
        !m.richContent?.length &&
        !m.pendingActions?.length
      ) {
        return false;
      }
      return true;
    })
  );

  /**
   * True once the assistant's streamed answer has started filling in — used to
   * hide the "typing" dots so they don't double up with the growing bubble.
   */
  readonly isStreamingReply = computed<boolean>(() => {
    const msgs = this.visibleMessages();
    const last = msgs[msgs.length - 1];
    return !!last && last.role === 'assistant' && last.content.length > 0;
  });

  /**
   * Tool names the backend reported for the reply currently being
   * produced — drives the "checking your data…" label under the typing
   * dots, so tool rounds (where most of a slow turn's time goes) no
   * longer look like a hang.
   */
  readonly activeTools = computed<string[] | null>(() => {
    if (!this.sending()) return null;
    const msgs = this.messages();
    const last = msgs[msgs.length - 1];
    if (
      last &&
      last.role === 'assistant' &&
      last.streaming === true &&
      last.content.length === 0 &&
      last.toolsInProgress &&
      last.toolsInProgress.length > 0
    ) {
      return last.toolsInProgress;
    }
    return null;
  });

  readonly canSend = computed(
    () =>
      !this.sending() &&
      (this.draft().trim().length > 0 || this.pendingFiles().length > 0)
  );

  /**
   * Opening chips for the current workspace type.
   *
   * The table itself lives in `chat-suggestions.util.ts` so it can be tested
   * without standing up the component — see its spec for why a syndic must
   * not be offered the owner questions.
   */
  readonly suggestions = computed<readonly ChatSuggestion[]>(() =>
    suggestionsForWorkspaceType(this.workspaceStore.currentWorkspace()?.type)
  );

  /**
   * The empty state tells the user what to ask, and the placeholder repeats
   * it. Both said "a lease's rent, a property's expenses" to every workspace
   * — including the two that have neither.
   */
  readonly emptyHintKey = computed(() =>
    emptyHintKeyFor(this.workspaceStore.currentWorkspace()?.type)
  );

  readonly inputPlaceholderKey = computed(() =>
    inputPlaceholderKeyFor(this.workspaceStore.currentWorkspace()?.type)
  );

  /** Progress label under typing dots (phase 1 without per-tool stream). */
  readonly typingStatusKey = computed<string>(() => {
    if (!this.sending()) return '';
    if (this.activeTools()?.length) {
      return 'aiAssistant.status.checkingData';
    }
    return 'aiAssistant.status.readingData';
  });

  async useSuggestion(messageKey: string): Promise<void> {
    if (!messageKey) return;
    await this.assistant.sendMessage(
      this.translate.instant(messageKey),
      this.buildPageContext()
    );
  }

  renderMarkdown(content: string): SafeHtml {
    return this.sanitizer.sanitizeTrusted(markdownToHtml(content));
  }

  trackBlock(_index: number, block: RichContentBlock): string {
    if (block.kind === 'form-fields') return `${block.kind}:${block.formId}`;
    if (block.kind === 'entity-match') return `${block.kind}:${block.entityId}`;
    return `${block.kind}:${_index}`;
  }

  async onChoiceSelected(option: ChoiceOption): Promise<void> {
    const text = option.message ?? option.label;
    if (!text) return;
    await this.assistant.sendMessage(text, this.buildPageContext());
  }

  async onFormSubmitted(payload: {
    formId: string;
    toolName?: string;
    values: Record<string, unknown>;
  }): Promise<void> {
    const parts = Object.entries(payload.values)
      .filter(([, v]) => v !== '' && v !== null && v !== undefined)
      .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`);
    if (parts.length === 0) return;
    const intro = payload.toolName ? `For ${payload.toolName}: ` : '';
    await this.assistant.sendMessage(
      `${intro}${parts.join(', ')}`,
      this.buildPageContext()
    );
  }

  async onQuestionAnswered(answer: string): Promise<void> {
    if (!answer) return;
    await this.assistant.sendMessage(answer, this.buildPageContext());
  }

  /**
   * One-click overdue reminder: stage `tenants_sendPaymentReminder`
   * without re-asking the LLM.
   */
  async onDataTableRowAction(action: DataTableRowAction): Promise<void> {
    if (action.kind === 'remind-batch') {
      const targets = (action.targets ?? []).slice(0, 10);
      if (targets.length === 0) return;
      await this.assistant.proposeAction('tenants_sendPaymentRemindersBatch', {
        targets
      });
      return;
    }
    if (action.kind !== 'remind') return;
    if (!action.tenantId) return;
    await this.assistant.proposeAction('tenants_sendPaymentReminder', {
      tenantId: action.tenantId,
      ...(action.contractId ? { contractId: action.contractId } : {})
    });
  }

  switchConversation(conversationId: string): void {
    this.assistant.switchToConversation(conversationId);
  }

  async deleteConversation(
    conversationId: string,
    event: MouseEvent
  ): Promise<void> {
    event.stopPropagation();
    event.preventDefault();
    await this.assistant.deleteConversation(conversationId);
  }

  toggleTemporary(): void {
    this.assistant.setTemporaryMode(!this.temporaryMode());
  }

  async copyMessage(message: ChatMessage): Promise<void> {
    try {
      await navigator.clipboard.writeText(message.content);
      this.copiedMessageId.set(message.id);
      setTimeout(() => {
        if (this.copiedMessageId() === message.id) {
          this.copiedMessageId.set(null);
        }
      }, 1500);
    } catch {
      // best-effort
    }
  }

  readonly feedbackDraft = signal<{
    message: ChatMessage;
    reasons: string[];
    comment: string;
  } | null>(null);

  readonly feedbackReasonKeys = [
    'wrongData',
    'wrongAction',
    'unclear',
    'other'
  ] as const;

  /** Human label of the tools used in a turn (never the raw tool names). */
  toolsLabel(tools: readonly string[]): string {
    const labels = tools.map((name) => {
      const key = `aiAssistant.tools.${name}`;
      const label = this.translate.instant(key);
      if (label !== key) return label;
      // Unknown tool: humanise "domain_actionName" instead of showing it raw.
      return name
        .replace(/^[a-z]+_/, '')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .toLowerCase();
    });
    return [...new Set(labels)].join(', ');
  }

  /** Message ids whose feedback was recorded (drives the "sent" state). */
  readonly feedbackSentIds = signal<ReadonlySet<string>>(new Set());

  @ViewChild('feedbackSheet') private feedbackSheet?: ElementRef<HTMLElement>;
  /** Element that opened the sheet, to give focus back on close. */
  private feedbackOpener: HTMLElement | null = null;

  private markFeedbackSent(messageId: string): void {
    this.feedbackSentIds.update((s) => new Set(s).add(messageId));
  }

  async rateMessage(message: ChatMessage, rating: 'up' | 'down'): Promise<void> {
    if (this.feedbackSentIds().has(message.id)) return;
    if (rating === 'up') {
      const sent = await this.assistant.submitFeedback({
        messageId: message.id,
        rating,
        messageExcerpt: message.content.slice(0, 400),
        toolsUsed: message.toolsUsed
      });
      if (sent) this.markFeedbackSent(message.id);
      return;
    }
    this.feedbackOpener = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    this.feedbackDraft.set({ message, reasons: [], comment: '' });
    // Move focus into the sheet once it is rendered.
    setTimeout(() => this.feedbackSheet?.nativeElement.focus());
  }

  toggleFeedbackReason(key: string): void {
    const draft = this.feedbackDraft();
    if (!draft) return;
    const reasons = draft.reasons.includes(key)
      ? draft.reasons.filter((r) => r !== key)
      : [...draft.reasons, key];
    this.feedbackDraft.set({ ...draft, reasons });
  }

  setFeedbackComment(comment: string): void {
    const draft = this.feedbackDraft();
    if (!draft) return;
    this.feedbackDraft.set({ ...draft, comment });
  }

  cancelFeedback(): void {
    this.feedbackDraft.set(null);
    this.feedbackOpener?.focus();
    this.feedbackOpener = null;
  }

  async submitFeedbackDown(): Promise<void> {
    const draft = this.feedbackDraft();
    if (!draft) return;
    const sent = await this.assistant.submitFeedback({
      messageId: draft.message.id,
      rating: 'down',
      reasons: draft.reasons,
      comment: draft.comment.trim() || undefined,
      messageExcerpt: draft.message.content.slice(0, 400),
      toolsUsed: draft.message.toolsUsed
    });
    // DESIGN.md §13: a failed submit never wipes the form — keep the sheet open.
    if (!sent) return;
    this.markFeedbackSent(draft.message.id);
    this.cancelFeedback();
  }

  async retry(): Promise<void> {
    if (!this.canRetry()) return;
    await this.assistant.retryLastMessage();
  }

  conversationLabel(c: {
    title: string | null;
    firstUserMessage?: string | null;
    id: string;
  }): string {
    if (c.title) return c.title;
    if (c.firstUserMessage) {
      const t = c.firstUserMessage.trim();
      return t.length > 60 ? t.slice(0, 57).trimEnd() + '…' : t;
    }
    return `Chat ${c.id.slice(0, 6)}`;
  }

  /**
   * Human-friendly relative time — used in the history list so users
   * can scan "2h ago", "Yesterday", "May 12" without parsing full
   * timestamps.
   */
  relativeTime(d: Date | null): string {
    if (!d) return '';
    const now = Date.now();
    const ts = d.getTime();
    const diffMs = now - ts;
    const t = this.translate;
    const minutes = Math.round(diffMs / 60_000);
    if (minutes < 1) return t.instant('aiAssistant.relativeTime.justNow');
    if (minutes < 60) return t.instant('aiAssistant.relativeTime.minutesAgo', { n: minutes });
    const hours = Math.round(minutes / 60);
    if (hours < 24) return t.instant('aiAssistant.relativeTime.hoursAgo', { n: hours });
    const days = Math.round(hours / 24);
    if (days === 1) return t.instant('aiAssistant.relativeTime.yesterday');
    if (days < 7) return t.instant('aiAssistant.relativeTime.daysAgo', { n: days });
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return d.toLocaleDateString(
      // The active UI language, not `undefined` — `undefined` resolves to the
      // BROWSER locale and ignores both LOCALE_ID and ngx-translate.
      this.activeLocale(),
      sameYear
        ? { month: 'short', day: 'numeric' }
        : { month: 'short', day: 'numeric', year: 'numeric' }
    );
  }

  /** Title of a pending action card, falling back when a tool has no key yet. */
  actionTitle(toolName: string): string {
    const key = `aiAssistant.actions.${toolName}.title`;
    const translated = this.translate.instant(key);
    // ngx-translate echoes the key back when it is missing — that is exactly
    // what used to reach the screen as "aiAssistant.actions.expenses_create.title".
    return translated === key
      ? this.translate.instant('aiAssistant.actions.fallbackTitle')
      : translated;
  }

  private activeLocale(): string {
    return this.translate.currentLang() || this.translate.getFallbackLang() || 'fr-BE';
  }

  private currentRoute = '';
  private shouldScroll = false;

  constructor() {
    this.currentRoute = this.router.url;
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe((e) => {
        this.currentRoute = (e as NavigationEnd).urlAfterRedirects;
      });

    effect(() => {
      this.messages();
      // Follow new content only when the user is already reading the
      // bottom — if they scrolled up to re-read something, a streaming
      // reply must not yank them back down on every token.
      this.shouldScroll = this.isNearBottom();
    });

    // `SupportStore.requestOpen()` is a counter, not a boolean: the bubble can
    // legitimately be clicked twice, and a boolean would only fire once.
    // Skips the initial 0 so the panel does not pop open on load — DESIGN.md
    // §13.7, nothing opens without a gesture.
    effect(() => {
      if (this.support.openRequest() > 0) {
        this.openSupport();
      }
    });

    effect(() => {
      if (this.isOpen()) {
        queueMicrotask(() => {
          this.panelTextarea?.nativeElement?.focus();
        });
        const prefill = this.assistant.consumePrefill();
        if (prefill) {
          this.draft.set(prefill);
        }
      }
    });
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.shouldScroll = false;
      // 'smooth' on every streamed token piles up animations and judders;
      // jump instantly while a reply streams, animate for discrete events.
      this.messagesEnd?.nativeElement?.scrollIntoView({
        behavior: this.isStreamingReply() ? 'auto' : 'smooth'
      });
    }
  }

  /**
   * Whether the messages viewport is scrolled (near) to its end. Before
   * the container renders, treat as "at bottom" so the first messages
   * still auto-scroll.
   */
  private isNearBottom(): boolean {
    const el = this.messagesContainer?.nativeElement;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }

  open(): void {
    this.assistant.open();
  }

  close(): void {
    this.assistant.close();
    this.supportOpen.set(false);
    this.isFullscreen.set(false);
  }

  /** Bring the panel up on the Support tab. Used by the bubble and the store. */
  openSupport(): void {
    this.supportOpen.set(true);
    this.activeTab.set('support');
  }

  /**
   * Hand the conversation over to a human (#291).
   *
   * The transcript goes with it — that is the whole point: the user has already
   * explained their problem once, and making them retype it is exactly the
   * frustration this button exists to end. But an assistant conversation can
   * carry tenant names, amounts and addresses, so it is NOT attached silently:
   * the dialog says how many messages will be sent, and the user can decline
   * and open an empty ticket instead.
   *
   * The dialog is legitimate under DESIGN.md §13.7 — it answers a click, it is
   * not something the app opened on its own.
   */
  async handoffToHuman(): Promise<void> {
    const transcript = this.buildTranscript();
    const choice = await this.feedback.confirm.basic({
      title: this.translate.instant('support.handoff.title'),
      message: this.translate.instant('support.handoff.message', {
        count: transcript.messageCount
      }),
      confirmText: this.translate.instant('support.handoff.confirm'),
      cancelText: this.translate.instant('support.handoff.cancel'),
      icon: 'support_agent'
    });
    if (!choice) return;

    this.openSupport();
    this.support.startHandoff({
      transcript: transcript.text,
      conversationId: this.currentConversationId() ?? undefined,
      subject: transcript.firstUserLine
    });
  }

  /** True once there is something worth handing over. */
  readonly canHandoff = computed(() => this.visibleMessages().length > 0);

  /**
   * Flatten the tail of the conversation into plain text.
   *
   * The TAIL, not the whole thing: a long session would blow past the server's
   * ceiling and be truncated mid-sentence, and the useful part of a failed
   * exchange is always its end.
   *
   * @returns The transcript, how many messages it holds, and the user's first
   *   line — which makes a far better ticket subject than "Assistant".
   */
  private buildTranscript(): { text: string; messageCount: number; firstUserLine: string } {
    const messages = this.visibleMessages().slice(-HANDOFF_MAX_MESSAGES);
    const text = messages
      .map(m => `${m.role === 'user' ? '👤' : '🤖'} ${m.content}`)
      .join('\n\n');
    const firstUser = messages.find(m => m.role === 'user')?.content ?? '';
    return {
      text,
      messageCount: messages.length,
      firstUserLine: firstUser.slice(0, 80).trim()
    };
  }

  selectTab(tab: PanelTab): void {
    this.activeTab.set(tab);
  }

  toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  toggleFullscreen(): void {
    this.isFullscreen.update((v) => !v);
  }

  newConversation(): void {
    this.assistant.startNewConversation();
    this.draft.set('');
  }

  onTextareaKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      void this.send();
    }
  }

  async send(): Promise<void> {
    if (!this.canSend()) return;
    const text = this.draft();
    const files = this.pendingFiles();
    // Clear composer only after a successful hand-off — DESIGN.md §13:
    // a failed submit must never wipe the draft or staged files.
    this.attachmentError.set(null);

    let attachments: ChatAttachmentRef[] | undefined;
    if (files.length > 0) {
      const wsId = this.workspaceStore.currentWorkspaceId();
      if (!wsId) {
        this.attachmentError.set(this.translate.instant('aiAssistant.errors.noWorkspace'));
        return;
      }
      const convId = this.currentConversationId() ?? `draft-${Date.now()}`;
      try {
        attachments = await this.attachmentService.uploadAll(
          wsId,
          convId,
          files
        );
      } catch {
        this.attachmentError.set(this.translate.instant('aiAssistant.errors.uploadFailed'));
        return;
      }
    }
    this.draft.set('');
    this.pendingFiles.set([]);
    await this.assistant.sendMessage(text, this.buildPageContext(), {
      attachments
    });
    // DESIGN.md §13 — restore draft if the turn failed.
    if (this.lastError()) {
      this.draft.set(text);
      if (files.length > 0) this.pendingFiles.set(files);
    }
  }

  onFilesPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    this.stageFiles(Array.from(input.files));
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    if (!this.hasFilePayload(event)) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingFiles.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingFiles.set(false);
  }

  onDrop(event: DragEvent): void {
    if (!this.hasFilePayload(event)) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingFiles.set(false);
    const list = event.dataTransfer?.files;
    if (!list?.length) return;
    this.stageFiles(Array.from(list));
  }

  private hasFilePayload(event: DragEvent): boolean {
    const types = event.dataTransfer?.types;
    if (!types) return false;
    return Array.from(types).includes('Files');
  }

  private stageFiles(incoming: File[]): void {
    const combined = [...this.pendingFiles(), ...incoming];
    const { accepted, errors } = this.attachmentService.validate(combined);
    this.pendingFiles.set(accepted);
    this.attachmentError.set(
      errors.length > 0
        ? this.translate.instant('aiAssistant.errors.attachmentInvalid')
        : null
    );
  }

  removePendingFile(index: number): void {
    const next = this.pendingFiles().slice();
    next.splice(index, 1);
    this.pendingFiles.set(next);
    this.attachmentError.set(null);
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${Math.round(kb)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  }

  fileIcon(mimeType: string): string {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType === 'application/pdf') return 'picture_as_pdf';
    if (mimeType.startsWith('text/')) return 'description';
    return 'attach_file';
  }

  isResolvingAction(actionId: string): boolean {
    return this.resolvingActionId() === actionId;
  }

  trackAction(_index: number, action: PendingActionEmbed): string {
    return action.actionId;
  }

  async confirmAction(actionId: string): Promise<void> {
    await this.assistant.confirmAction(actionId);
  }

  async rejectAction(actionId: string): Promise<void> {
    await this.assistant.rejectAction(actionId);
  }

  @HostListener('document:keydown.escape')
  onDocumentEscape(): void {
    // Escape closes the feedback sheet first, not the whole panel.
    if (this.feedbackDraft()) {
      this.cancelFeedback();
      return;
    }
    if (this.panelOpen()) this.close();
  }

  /**
   * Close the panel when the user clicks anywhere outside it — clearer UX
   * than forcing the close button or Escape. We keep it open when the
   * click lands:
   *   - inside this component's host (the panel) — panel clicks must not
   *     self-close;
   *   - inside a CDK overlay (the history `mat-menu`, tooltips) which
   *     render outside the host but are logically part of the assistant;
   *   - on a function-route sidenav item (e.g. « Assistant Virtuel »), which
   *     stops propagation itself so its toggle is not immediately undone.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.panelOpen()) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (this.host.nativeElement.contains(target)) return;
    if (target.closest('.cdk-overlay-container')) return;
    this.close();
  }

  private buildPageContext(): ChatPageContext | undefined {
    if (!this.currentRoute) return undefined;
    const ctx: ChatPageContext = { route: this.currentRoute };
    const match = this.currentRoute.match(
      /\/(properties|contracts|tenants|interventions|loans|expenses|documents)\/([^/?]+)/
    );
    if (match) {
      ctx.focusedEntityType = singularize(match[1]);
      ctx.focusedEntityId = match[2];
    }
    // A pilot page (property/contract detail, cockpit card) may have pushed
    // richer context (tab, filters, visible rows) via the provider — it wins
    // over the route regex, which stays the fallback for pages that haven't
    // opted in (#224).
    return { ...ctx, ...this.pageContextProvider.context() };
  }
}

function singularize(plural: string): string {
  if (plural === 'properties') return 'property';
  if (plural.endsWith('s')) return plural.slice(0, -1);
  return plural;
}
